import React from 'react';

import { DeleteIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button.js';
import { Button } from '@vlabs/uikit/components/controls/button/Button.js';
import { toast } from 'react-toastify';

import { DeleteRolesForm } from '@vlabs/monokit/entities/roles';
import type { DeleteRolesFormValues } from '@vlabs/monokit/entities/roles';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { deleteRolesAndDetachUsers } from './deleteRolesAndDetachUsers';
import { useDeleteRoleTranslation } from './i18n';
import type { DeleteRoles } from './types';

interface DeleteRoleButtonProps extends DeleteRoles, Omit<ButtonProps, 'onClick'> { }

const displayName = 'DeleteRolesButton';
const testid = buildTestid(displayName);

function DeleteRolesButton({
  roleIds,
  onDeleteRoles = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: DeleteRoleButtonProps): React.JSX.Element {
  const { t } = useDeleteRoleTranslation();
  const modal = useModal({ size: 'small' });

  const handleSubmit = async (values: DeleteRolesFormValues): Promise<void> => {
    try {
      await deleteRolesAndDetachUsers(values);
      toast.success(t('toast_success'));
      modal.close();
      onDeleteRoles?.();
    } catch {
      toast.error(t('toast_error'));
    }
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <DeleteRolesForm
          defaultValues={{ roleIds }}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <Button
        icon={<DeleteIcon />}
        kind="negative"
        onClick={modal.open}
        variant="outlined"
        {...testid(dataTestId)}
        {...props}
      >
        {t('delete')}
      </Button>
    </React.Fragment>
  );
}

DeleteRolesButton.displayName = displayName;

export { DeleteRolesButton };
